import React, { useState, useEffect, useMemo } from 'react';
import { useMsal, useAccount } from "@azure/msal-react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, ScatterChart, Scatter
} from 'recharts';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { MaterialReactTable } from "material-react-table";

import CONFIG from '../config';
import * as Utils from '../lib/utils';

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548'];
const RANGE_BUCKET_ORDER = ['Below Range', '0-25%', '25-50%', '50-75%', '75-100%', 'Above Range', 'No Range'];
const RANGE_BUCKET_COLORS = {
    'Below Range': '#F44336',
    '0-25%': '#FF9800',
    '25-50%': '#FFC107',
    '50-75%': '#4CAF50',
    '75-100%': '#2196F3',
    'Above Range': '#9C27B0',
    'No Range': '#9E9E9E'
};


function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`metrics-tabpanel-${index}`} aria-labelledby={`metrics-tab-${index}`}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

const EmployeeMetrics = (props) => {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [metricsData, setMetricsData] = useState([]);
    const [salaryHistoryData, setSalaryHistoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // Filters
    const [filterDiscipline, setFilterDiscipline] = useState('All');
    const [filterLocation, setFilterLocation] = useState('All');
    const [filterJobLevel, setFilterJobLevel] = useState('All');
    const [filterSalaryGroup, setFilterSalaryGroup] = useState('All');

    const isNotProd = CONFIG.debug || CONFIG.staging;
    const impUser = isNotProd ? localStorage.getItem("impersonated_user") : "";

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const [rangeData, historyData] = await Promise.all([
                    Utils.getData('/api/employee_metrics/range_penetration', instance, account),
                    Utils.getData('/api/employee_metrics/salary_history', instance, account)
                ]);

                setMetricsData(Array.isArray(rangeData) ? rangeData : []);
                setSalaryHistoryData(Array.isArray(historyData) ? historyData : []);
            } catch (err) {
                console.error("Error fetching metrics:", err);
                setError("Failed to load employee metrics data. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        if (account) {
            fetchData();
        }
    }, [account]);

    // Derive filter options from data
    const filterOptions = useMemo(() => {
        const disciplines = [...new Set(metricsData.map(e => e.discipline).filter(Boolean))].sort();
        const locations = [...new Set(metricsData.map(e => e.location || e.budgeted_office_name).filter(Boolean))].sort();
        const jobLevels = [...new Set(metricsData.map(e => e.job_level).filter(Boolean))].sort();
        const salaryGroups = [...new Set(metricsData.map(e => e.group_name).filter(Boolean))].sort();
        return { disciplines, locations, jobLevels, salaryGroups };
    }, [metricsData]);

    // Apply filters
    const filteredData = useMemo(() => {
        return metricsData.filter(emp => {
            if (filterDiscipline !== 'All' && emp.discipline !== filterDiscipline) return false;
            if (filterLocation !== 'All' && (emp.location || emp.budgeted_office_name) !== filterLocation) return false;
            if (filterJobLevel !== 'All' && emp.job_level !== filterJobLevel) return false;
            if (filterSalaryGroup !== 'All' && emp.group_name !== filterSalaryGroup) return false;
            return true;
        });
    }, [metricsData, filterDiscipline, filterLocation, filterJobLevel, filterSalaryGroup]);


    // ===== 1. RANGE PENETRATION DATA =====
    const rangePenetrationData = useMemo(() => {
        const buckets = {};
        RANGE_BUCKET_ORDER.forEach(b => { buckets[b] = 0; });
        filteredData.forEach(emp => {
            const bucket = emp.range_bucket || 'No Range';
            if (buckets[bucket] !== undefined) buckets[bucket]++;
        });
        return RANGE_BUCKET_ORDER.map(name => ({ name, count: buckets[name] })).filter(d => d.count > 0);
    }, [filteredData]);

    // Range penetration by job level
    const rangePenetrationByLevel = useMemo(() => {
        const levels = {};
        filteredData.forEach(emp => {
            const level = emp.job_level || 'Unknown';
            if (!levels[level]) levels[level] = { level, 'Below Range': 0, '0-25%': 0, '25-50%': 0, '50-75%': 0, '75-100%': 0, 'Above Range': 0 };
            const bucket = emp.range_bucket || 'No Range';
            if (bucket !== 'No Range' && levels[level][bucket] !== undefined) {
                levels[level][bucket]++;
            }
        });
        return Object.values(levels);
    }, [filteredData]);

    // ===== 2. RANGE SATURATION RISK =====
    const saturationRiskData = useMemo(() => {
        const atRisk = filteredData.filter(emp => {
            const penetration = parseFloat(emp.range_penetration_pct);
            // At risk: high penetration with relatively low tenure (moving too fast)
            // Or: high penetration regardless (approaching cap)
            return penetration >= 75 && !isNaN(penetration);
        });
        const tenureBuckets = { 'Less than 1 year': 0, '1-3 years': 0, '3-5 years': 0, '5-10 years': 0, '10+ years': 0 };
        atRisk.forEach(emp => {
            const bucket = emp.tenure_bucket || '10+ years';
            if (tenureBuckets[bucket] !== undefined) tenureBuckets[bucket]++;
        });
        return Object.entries(tenureBuckets).map(([name, count]) => ({ name, count }));
    }, [filteredData]);

    // ===== 3. PAY COMPRESSION DETECTION =====
    const compressionData = useMemo(() => {
        // Group by position_title, compare new hires vs incumbents
        const groups = {};
        filteredData.forEach(emp => {
            const key = emp.position_title || 'Unknown';
            if (!groups[key]) groups[key] = [];
            groups[key].push(emp);
        });

        const compressionFlags = [];
        Object.entries(groups).forEach(([title, employees]) => {
            if (employees.length < 2) return;
            const withSalary = employees.filter(e => e.current_ft_annualized_salary > 0);
            if (withSalary.length < 2) return;

            const sorted = withSalary.sort((a, b) => parseFloat(b.tenure_years) - parseFloat(a.tenure_years));
            const avgTenured = sorted.filter(e => parseFloat(e.tenure_years) >= 3);
            const avgNew = sorted.filter(e => parseFloat(e.tenure_years) < 2);

            if (avgTenured.length > 0 && avgNew.length > 0) {
                const avgTenuredSalary = avgTenured.reduce((sum, e) => sum + parseFloat(e.current_ft_annualized_salary), 0) / avgTenured.length;
                const avgNewSalary = avgNew.reduce((sum, e) => sum + parseFloat(e.current_ft_annualized_salary), 0) / avgNew.length;
                const ratio = avgNewSalary / avgTenuredSalary;

                if (ratio > 0.90) {
                    compressionFlags.push({
                        position_title: title,
                        employee_count: withSalary.length,
                        avg_tenured_salary: Math.round(avgTenuredSalary),
                        avg_new_salary: Math.round(avgNewSalary),
                        compression_ratio: Math.round(ratio * 100),
                        risk_level: ratio > 0.97 ? 'High' : ratio > 0.93 ? 'Medium' : 'Low'
                    });
                }
            }
        });
        return compressionFlags.sort((a, b) => b.compression_ratio - a.compression_ratio);
    }, [filteredData]);


    // ===== 4. SALARY MOVEMENT VELOCITY =====
    const velocityData = useMemo(() => {
        // Group salary history by employee, calculate avg annual increase
        const employeeHistory = {};
        salaryHistoryData.forEach(record => {
            const key = record.employee_hris_id;
            if (!employeeHistory[key]) {
                employeeHistory[key] = {
                    name: `${record.first_name} ${record.last_name}`,
                    position: record.position_title,
                    records: []
                };
            }
            employeeHistory[key].records.push(record);
        });

        const velocities = [];
        Object.entries(employeeHistory).forEach(([hrisId, emp]) => {
            if (emp.records.length < 2) return;
            const sorted = emp.records.sort((a, b) => new Date(a.salary_change_effective_date) - new Date(b.salary_change_effective_date));
            const firstSalary = parseFloat(sorted[0].historical_salary);
            const lastSalary = parseFloat(sorted[sorted.length - 1].historical_salary);
            const firstDate = new Date(sorted[0].salary_change_effective_date);
            const lastDate = new Date(sorted[sorted.length - 1].salary_change_effective_date);
            const yearsDiff = (lastDate - firstDate) / (365.25 * 24 * 60 * 60 * 1000);

            if (yearsDiff > 0 && firstSalary > 0) {
                const totalGrowthPct = ((lastSalary - firstSalary) / firstSalary) * 100;
                const annualVelocity = totalGrowthPct / yearsDiff;
                velocities.push({
                    employee_hris_id: hrisId,
                    name: emp.name,
                    position: emp.position,
                    total_growth_pct: Math.round(totalGrowthPct * 10) / 10,
                    annual_velocity_pct: Math.round(annualVelocity * 10) / 10,
                    years_tracked: Math.round(yearsDiff * 10) / 10,
                    num_changes: sorted.length,
                    promotions: sorted.filter(r => r.is_promotion).length
                });
            }
        });
        return velocities.sort((a, b) => b.annual_velocity_pct - a.annual_velocity_pct);
    }, [salaryHistoryData]);

    // Velocity distribution for chart
    const velocityDistribution = useMemo(() => {
        const buckets = { '0-2%': 0, '2-4%': 0, '4-6%': 0, '6-8%': 0, '8-10%': 0, '10%+': 0 };
        velocityData.forEach(v => {
            const vel = v.annual_velocity_pct;
            if (vel <= 2) buckets['0-2%']++;
            else if (vel <= 4) buckets['2-4%']++;
            else if (vel <= 6) buckets['4-6%']++;
            else if (vel <= 8) buckets['6-8%']++;
            else if (vel <= 10) buckets['8-10%']++;
            else buckets['10%+']++;
        });
        return Object.entries(buckets).map(([name, count]) => ({ name, count }));
    }, [velocityData]);

    // ===== 5. LEVEL-BASED DISTRIBUTION =====
    const levelDistribution = useMemo(() => {
        const levels = {};
        filteredData.forEach(emp => {
            const level = emp.job_level || 'Unknown';
            if (!levels[level]) levels[level] = { level, count: 0, avgPenetration: 0, totalPenetration: 0 };
            levels[level].count++;
            const pen = parseFloat(emp.range_penetration_pct);
            if (!isNaN(pen)) {
                levels[level].totalPenetration += pen;
            }
        });
        Object.values(levels).forEach(l => {
            l.avgPenetration = l.count > 0 ? Math.round(l.totalPenetration / l.count) : 0;
        });
        return Object.values(levels).sort((a, b) => b.count - a.count);
    }, [filteredData]);


    // ===== 6. OUTLIER IDENTIFICATION =====
    const outlierData = useMemo(() => {
        const outliers = [];
        filteredData.forEach(emp => {
            const penetration = parseFloat(emp.range_penetration_pct);
            const tenure = parseFloat(emp.tenure_years);
            const flags = [];

            if (!isNaN(penetration) && !isNaN(tenure)) {
                // Below range for tenure/level
                if (penetration < 15 && tenure > 5) {
                    flags.push('Below range for tenure');
                }
                // Above range without level adjustment
                if (penetration > 100) {
                    flags.push('Above range - needs level review');
                }
                // Stagnant in mid-range
                if (penetration >= 40 && penetration <= 60 && tenure > 7) {
                    flags.push('Stagnant mid-range');
                }
                // High penetration with low tenure (potential compression source)
                if (penetration > 80 && tenure < 2) {
                    flags.push('High penetration new hire');
                }
            }

            if (flags.length > 0) {
                outliers.push({
                    id: emp.id,
                    name: `${emp.first_name} ${emp.last_name}`,
                    position_title: emp.position_title,
                    job_level: emp.job_level,
                    tenure_years: tenure,
                    range_penetration_pct: penetration,
                    current_salary: parseFloat(emp.current_ft_annualized_salary),
                    range_minimum: parseFloat(emp.range_minimum),
                    range_maximum: parseFloat(emp.range_maximum),
                    flags: flags,
                    flag_count: flags.length,
                    severity: flags.length >= 2 ? 'High' : 'Medium'
                });
            }
        });
        return outliers.sort((a, b) => b.flag_count - a.flag_count);
    }, [filteredData]);

    // ===== 7. PROMOTION IMPACT =====
    const promotionImpactData = useMemo(() => {
        // Find employees with promotions in salary history and analyze range reset
        const employeeHistory = {};
        salaryHistoryData.forEach(record => {
            const key = record.employee_hris_id;
            if (!employeeHistory[key]) employeeHistory[key] = [];
            employeeHistory[key].push(record);
        });

        const impacts = [];
        Object.entries(employeeHistory).forEach(([hrisId, records]) => {
            const sorted = records.sort((a, b) => new Date(a.salary_change_effective_date) - new Date(b.salary_change_effective_date));
            for (let i = 1; i < sorted.length; i++) {
                if (sorted[i].is_promotion) {
                    const preSalary = parseFloat(sorted[i - 1].historical_salary);
                    const postSalary = parseFloat(sorted[i].historical_salary);
                    if (preSalary > 0 && postSalary > 0) {
                        const increasePct = ((postSalary - preSalary) / preSalary) * 100;
                        impacts.push({
                            name: `${sorted[i].first_name} ${sorted[i].last_name}`,
                            from_position: sorted[i - 1].historical_position || sorted[i - 1].position_title,
                            to_position: sorted[i].historical_position || sorted[i].position_title,
                            date: sorted[i].salary_change_effective_date,
                            pre_salary: Math.round(preSalary),
                            post_salary: Math.round(postSalary),
                            increase_pct: Math.round(increasePct * 10) / 10,
                            year: sorted[i].change_year
                        });
                    }
                }
            }
        });
        return impacts.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [salaryHistoryData]);

    // Promotion increase distribution
    const promotionDistribution = useMemo(() => {
        const buckets = { '0-5%': 0, '5-10%': 0, '10-15%': 0, '15-20%': 0, '20%+': 0 };
        promotionImpactData.forEach(p => {
            const pct = p.increase_pct;
            if (pct <= 5) buckets['0-5%']++;
            else if (pct <= 10) buckets['5-10%']++;
            else if (pct <= 15) buckets['10-15%']++;
            else if (pct <= 20) buckets['15-20%']++;
            else buckets['20%+']++;
        });
        return Object.entries(buckets).map(([name, count]) => ({ name, count }));
    }, [promotionImpactData]);


    // ===== SUMMARY STATS =====
    const summaryStats = useMemo(() => {
        const withRange = filteredData.filter(e => e.range_penetration_pct !== null);
        const avgPenetration = withRange.length > 0
            ? Math.round(withRange.reduce((sum, e) => sum + parseFloat(e.range_penetration_pct), 0) / withRange.length)
            : 0;
        const aboveRange = filteredData.filter(e => e.range_bucket === 'Above Range').length;
        const belowRange = filteredData.filter(e => e.range_bucket === 'Below Range').length;
        return {
            totalEmployees: filteredData.length,
            avgPenetration,
            aboveRange,
            belowRange,
            compressionAlerts: compressionData.filter(c => c.risk_level === 'High').length,
            outlierCount: outlierData.length
        };
    }, [filteredData, compressionData, outlierData]);

    // ===== RENDER =====
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress size={60} />
                <Typography sx={{ ml: 2 }} variant="h6">Loading Employee Metrics...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', margin: '0 auto' }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>Employee Metrics Dashboard</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Compensation analytics, range penetration, and workforce health indicators
            </Typography>

            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Total Employees</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>{summaryStats.totalEmployees}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Avg Range Penetration</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>{summaryStats.avgPenetration}%</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Above Range</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#9C27B0' }}>{summaryStats.aboveRange}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Below Range</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#F44336' }}>{summaryStats.belowRange}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Compression Alerts</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: summaryStats.compressionAlerts > 0 ? '#F44336' : '#4CAF50' }}>
                            {summaryStats.compressionAlerts}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 150, flex: 1 }}>
                    <CardContent>
                        <Typography color="text.secondary" variant="body2">Outlier Flags</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: summaryStats.outlierCount > 0 ? '#FF9800' : '#4CAF50' }}>
                            {summaryStats.outlierCount}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>


            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Discipline</InputLabel>
                    <Select value={filterDiscipline} label="Discipline" onChange={(e) => setFilterDiscipline(e.target.value)}>
                        <MenuItem value="All">All Disciplines</MenuItem>
                        {filterOptions.disciplines.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Location</InputLabel>
                    <Select value={filterLocation} label="Location" onChange={(e) => setFilterLocation(e.target.value)}>
                        <MenuItem value="All">All Locations</MenuItem>
                        {filterOptions.locations.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Job Level</InputLabel>
                    <Select value={filterJobLevel} label="Job Level" onChange={(e) => setFilterJobLevel(e.target.value)}>
                        <MenuItem value="All">All Levels</MenuItem>
                        {filterOptions.jobLevels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Salary Group</InputLabel>
                    <Select value={filterSalaryGroup} label="Salary Group" onChange={(e) => setFilterSalaryGroup(e.target.value)}>
                        <MenuItem value="All">All Groups</MenuItem>
                        {filterOptions.salaryGroups.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Range Penetration" />
                    <Tab label="Saturation Risk" />
                    <Tab label="Pay Compression" />
                    <Tab label="Salary Velocity" />
                    <Tab label="Level Distribution" />
                    <Tab label="Outliers" />
                    <Tab label="Promotion Impact" />
                </Tabs>
            </Box>


            {/* TAB 0: Range Penetration */}
            <TabPanel value={activeTab} index={0}>
                <Typography variant="h6" sx={{ mb: 2 }}>Salary Range Penetration Distribution</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Where employees sit within their salary range. Healthy distribution shows most employees in the 25-75% zone.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 400 }}>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={rangePenetrationData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" name="Employees">
                                    {rangePenetrationData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={RANGE_BUCKET_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 350 }}>
                        <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                                <Pie data={rangePenetrationData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={120} label>
                                    {rangePenetrationData.map((entry, index) => (
                                        <Cell key={`pie-${index}`} fill={RANGE_BUCKET_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Range Penetration by Job Level</Typography>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={rangePenetrationByLevel}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="level" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Below Range" stackId="a" fill={RANGE_BUCKET_COLORS['Below Range']} />
                        <Bar dataKey="0-25%" stackId="a" fill={RANGE_BUCKET_COLORS['0-25%']} />
                        <Bar dataKey="25-50%" stackId="a" fill={RANGE_BUCKET_COLORS['25-50%']} />
                        <Bar dataKey="50-75%" stackId="a" fill={RANGE_BUCKET_COLORS['50-75%']} />
                        <Bar dataKey="75-100%" stackId="a" fill={RANGE_BUCKET_COLORS['75-100%']} />
                        <Bar dataKey="Above Range" stackId="a" fill={RANGE_BUCKET_COLORS['Above Range']} />
                    </BarChart>
                </ResponsiveContainer>
            </TabPanel>


            {/* TAB 1: Saturation Risk */}
            <TabPanel value={activeTab} index={1}>
                <Typography variant="h6" sx={{ mb: 2 }}>Range Saturation Risk Analysis</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Employees at 75%+ range penetration grouped by tenure. High penetration with low tenure indicates rapid movement toward range cap.
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={saturationRiskData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" name="At-Risk Employees" fill="#FF5722" />
                    </BarChart>
                </ResponsiveContainer>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Penetration vs Tenure Scatter</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Each dot is an employee. Upper-left quadrant (high penetration, low tenure) indicates saturation risk.
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                    <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="tenure" name="Tenure (years)" unit=" yrs" />
                        <YAxis type="number" dataKey="penetration" name="Range Penetration" unit="%" domain={[0, 120]} />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter
                            name="Employees"
                            data={filteredData.filter(e => e.range_penetration_pct !== null).map(e => ({
                                tenure: parseFloat(e.tenure_years),
                                penetration: parseFloat(e.range_penetration_pct),
                                name: `${e.first_name} ${e.last_name}`
                            }))}
                            fill="#2196F3"
                        />
                    </ScatterChart>
                </ResponsiveContainer>
            </TabPanel>

            {/* TAB 2: Pay Compression */}
            <TabPanel value={activeTab} index={2}>
                <Typography variant="h6" sx={{ mb: 2 }}>Pay Compression Detection</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Identifies roles where new hires (under 2 years) earn 90%+ of what tenured employees (3+ years) earn in the same position.
                    A compression ratio above 97% is flagged as high risk.
                </Typography>
                {compressionData.length === 0 ? (
                    <Alert severity="success" sx={{ mb: 2 }}>No significant pay compression detected across current filters.</Alert>
                ) : (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            {compressionData.filter(c => c.risk_level === 'High').length > 0 && (
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    {compressionData.filter(c => c.risk_level === 'High').length} role(s) with HIGH compression risk detected
                                </Alert>
                            )}
                        </Box>
                        <MaterialReactTable
                            columns={[
                                { accessorKey: 'position_title', header: 'Position', size: 200 },
                                { accessorKey: 'employee_count', header: 'Employees', size: 100 },
                                { accessorKey: 'avg_tenured_salary', header: 'Avg Tenured Salary', size: 150,
                                    Cell: ({ cell }) => `$${cell.getValue().toLocaleString()}` },
                                { accessorKey: 'avg_new_salary', header: 'Avg New Hire Salary', size: 150,
                                    Cell: ({ cell }) => `$${cell.getValue().toLocaleString()}` },
                                { accessorKey: 'compression_ratio', header: 'Compression %', size: 130,
                                    Cell: ({ cell }) => `${cell.getValue()}%` },
                                { accessorKey: 'risk_level', header: 'Risk', size: 100,
                                    Cell: ({ cell }) => (
                                        <Chip
                                            label={cell.getValue()}
                                            size="small"
                                            color={cell.getValue() === 'High' ? 'error' : cell.getValue() === 'Medium' ? 'warning' : 'default'}
                                        />
                                    )
                                }
                            ]}
                            data={compressionData}
                            enablePagination={false}
                            enableColumnFilters={false}
                            enableSorting={true}
                            initialState={{ density: 'compact' }}
                        />
                    </Box>
                )}
            </TabPanel>


            {/* TAB 3: Salary Velocity */}
            <TabPanel value={activeTab} index={3}>
                <Typography variant="h6" sx={{ mb: 2 }}>Salary Movement Velocity</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Average annual salary growth rate per employee, combining merit increases and promotions.
                    Typical healthy velocity is 3-6% annually.
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={velocityDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" label={{ value: 'Annual Growth Rate', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: 'Employees', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="count" name="Employees" fill="#3F51B5" />
                    </BarChart>
                </ResponsiveContainer>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Top Movers (Highest Annual Velocity)</Typography>
                <MaterialReactTable
                    columns={[
                        { accessorKey: 'name', header: 'Employee', size: 180 },
                        { accessorKey: 'position', header: 'Current Position', size: 200 },
                        { accessorKey: 'annual_velocity_pct', header: 'Annual Velocity %', size: 140,
                            Cell: ({ cell }) => `${cell.getValue()}%` },
                        { accessorKey: 'total_growth_pct', header: 'Total Growth %', size: 130,
                            Cell: ({ cell }) => `${cell.getValue()}%` },
                        { accessorKey: 'years_tracked', header: 'Years Tracked', size: 120 },
                        { accessorKey: 'num_changes', header: 'Salary Changes', size: 120 },
                        { accessorKey: 'promotions', header: 'Promotions', size: 100 }
                    ]}
                    data={velocityData.slice(0, 50)}
                    enablePagination={true}
                    enableColumnFilters={true}
                    enableSorting={true}
                    initialState={{ density: 'compact', pagination: { pageSize: 15 } }}
                />
            </TabPanel>

            {/* TAB 4: Level Distribution */}
            <TabPanel value={activeTab} index={4}>
                <Typography variant="h6" sx={{ mb: 2 }}>Level-Based Distribution Health</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Distribution of employees across job levels and their average range penetration.
                    Healthy organizations show a pyramid shape with lower penetration at entry levels.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 400 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Employee Count by Level</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={levelDistribution} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="level" width={100} />
                                <Tooltip />
                                <Bar dataKey="count" name="Employees" fill="#00897B" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 400 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Avg Range Penetration by Level</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={levelDistribution} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} unit="%" />
                                <YAxis type="category" dataKey="level" width={100} />
                                <Tooltip />
                                <Bar dataKey="avgPenetration" name="Avg Penetration %" fill="#5C6BC0" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </TabPanel>


            {/* TAB 5: Outliers */}
            <TabPanel value={activeTab} index={5}>
                <Typography variant="h6" sx={{ mb: 2 }}>Outlier Identification &amp; Range Mismatch Flags</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Employees flagged for range anomalies: below range for tenure, above range without level adjustment,
                    stagnant mid-range, or high-penetration new hires.
                </Typography>
                {outlierData.length === 0 ? (
                    <Alert severity="success">No outliers detected with current filters.</Alert>
                ) : (
                    <Box>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {outlierData.length} employee(s) flagged for review ({outlierData.filter(o => o.severity === 'High').length} high severity)
                        </Alert>
                        <MaterialReactTable
                            columns={[
                                { accessorKey: 'name', header: 'Employee', size: 160 },
                                { accessorKey: 'position_title', header: 'Position', size: 180 },
                                { accessorKey: 'job_level', header: 'Level', size: 100 },
                                { accessorKey: 'tenure_years', header: 'Tenure (yrs)', size: 110,
                                    Cell: ({ cell }) => cell.getValue().toFixed(1) },
                                { accessorKey: 'range_penetration_pct', header: 'Range %', size: 100,
                                    Cell: ({ cell }) => `${cell.getValue().toFixed(1)}%` },
                                { accessorKey: 'current_salary', header: 'Salary', size: 120,
                                    Cell: ({ cell }) => `$${cell.getValue().toLocaleString()}` },
                                { accessorKey: 'flags', header: 'Flags', size: 280,
                                    Cell: ({ cell }) => (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {cell.getValue().map((flag, i) => (
                                                <Chip key={i} label={flag} size="small" color="warning" variant="outlined" />
                                            ))}
                                        </Box>
                                    )
                                },
                                { accessorKey: 'severity', header: 'Severity', size: 90,
                                    Cell: ({ cell }) => (
                                        <Chip
                                            label={cell.getValue()}
                                            size="small"
                                            color={cell.getValue() === 'High' ? 'error' : 'warning'}
                                        />
                                    )
                                }
                            ]}
                            data={outlierData}
                            enablePagination={true}
                            enableColumnFilters={true}
                            enableSorting={true}
                            initialState={{ density: 'compact', pagination: { pageSize: 15 } }}
                        />
                    </Box>
                )}
            </TabPanel>

            {/* TAB 6: Promotion Impact */}
            <TabPanel value={activeTab} index={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>Promotion Impact on Range Reset</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    How promotions reposition employees within new ranges. Healthy promotions should provide meaningful salary increases
                    (typically 8-15%) that reset employees to a lower penetration in their new range.
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={promotionDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" label={{ value: 'Promotion Increase %', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="count" name="Promotions" fill="#E91E63" />
                    </BarChart>
                </ResponsiveContainer>

                {promotionImpactData.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Recent Promotions</Typography>
                        <MaterialReactTable
                            columns={[
                                { accessorKey: 'name', header: 'Employee', size: 160 },
                                { accessorKey: 'from_position', header: 'From Position', size: 180 },
                                { accessorKey: 'to_position', header: 'To Position', size: 180 },
                                { accessorKey: 'date', header: 'Date', size: 110,
                                    Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '' },
                                { accessorKey: 'pre_salary', header: 'Pre-Salary', size: 120,
                                    Cell: ({ cell }) => `$${cell.getValue().toLocaleString()}` },
                                { accessorKey: 'post_salary', header: 'Post-Salary', size: 120,
                                    Cell: ({ cell }) => `$${cell.getValue().toLocaleString()}` },
                                { accessorKey: 'increase_pct', header: 'Increase %', size: 110,
                                    Cell: ({ cell }) => `${cell.getValue()}%` }
                            ]}
                            data={promotionImpactData.slice(0, 50)}
                            enablePagination={true}
                            enableColumnFilters={true}
                            enableSorting={true}
                            initialState={{ density: 'compact', pagination: { pageSize: 15 } }}
                        />
                    </Box>
                )}
            </TabPanel>
        </Box>
    );
};

export default EmployeeMetrics;
